import { useState, useEffect } from 'react';
import { useAuthStore } from '../store';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea } from '@/components/ui/scroll-area';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Ticket, Loader2, Search, Percent, User, Calendar, Hash, Users, X } from 'lucide-react';

export default function CouponsPage() {
  const { api } = useAuthStore();
  const [coupons, setCoupons] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [customerSearchTerm, setCustomerSearchTerm] = useState('');
  
  const [formData, setFormData] = useState({
    code: '',
    discount_percent: '',
    customer_ids: [], // Mudou de customer_id para customer_ids (array)
    max_uses: '0',
    expires_at: ''
  });

  useEffect(() => {
    fetchCoupons();
    fetchCustomers();
  }, []);

  const fetchCoupons = async () => {
    try {
      const response = await api.get('/coupons');
      setCoupons(response.data);
    } catch (error) {
      toast.error('Erro ao carregar cupons');
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomers = async () => {
    try {
      const response = await api.get('/customers?limit=5000');
      const customersList = response.data.customers || response.data || [];
      setCustomers(Array.isArray(customersList) ? customersList : []);
    } catch (error) {
      console.error('Erro ao carregar clientes:', error);
      setCustomers([]);
    }
  };

  const openModal = (coupon = null) => {
    if (coupon) {
      setEditing(coupon);
      setFormData({
        code: coupon.code || '',
        discount_percent: coupon.discount_percent?.toString() || '',
        customer_ids: coupon.customer_ids || (coupon.customer_id ? [coupon.customer_id] : []),
        max_uses: coupon.max_uses?.toString() || '0',
        expires_at: coupon.expires_at ? coupon.expires_at.split('T')[0] : ''
      });
    } else {
      setEditing(null);
      setFormData({
        code: '',
        discount_percent: '',
        customer_ids: [],
        max_uses: '0',
        expires_at: ''
      });
    }
    setCustomerSearchTerm('');
    setModalOpen(true);
  };

  // Filtrar clientes pela busca
  const filteredCustomers = customers.filter(c => 
    c.name?.toLowerCase().includes(customerSearchTerm.toLowerCase()) ||
    c.phone?.includes(customerSearchTerm)
  );

  // Toggle cliente selecionado
  const toggleCustomer = (customerId) => {
    const newIds = formData.customer_ids.includes(customerId)
      ? formData.customer_ids.filter(id => id !== customerId)
      : [...formData.customer_ids, customerId];
    setFormData({ ...formData, customer_ids: newIds });
  };

  // Obter nome do cliente pelo ID
  const getCustomerName = (customerId) => {
    const customer = customers.find(c => c.id === customerId);
    return customer?.name || 'Cliente não encontrado';
  };

  const handleSubmit = async () => {
    if (!formData.code.trim()) {
      toast.error('Digite o código do cupom');
      return;
    }
    if (!formData.discount_percent || parseFloat(formData.discount_percent) <= 0) {
      toast.error('Digite uma porcentagem de desconto válida');
      return;
    }
    if (parseFloat(formData.discount_percent) > 100) {
      toast.error('Porcentagem não pode ser maior que 100%');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        code: formData.code.toUpperCase(),
        discount_percent: parseFloat(formData.discount_percent),
        customer_ids: formData.customer_ids.length > 0 ? formData.customer_ids : null,
        max_uses: parseInt(formData.max_uses) || 0,
        expires_at: formData.expires_at ? `${formData.expires_at}T23:59:59-03:00` : null
      };

      if (editing) {
        await api.put(`/coupons/${editing.id}`, payload);
        toast.success('Cupom atualizado!');
      } else {
        await api.post('/coupons', payload);
        toast.success('Cupom criado!');
      }
      
      setModalOpen(false);
      fetchCoupons();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar cupom');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (coupon) => {
    if (!confirm(`Excluir cupom "${coupon.code}"?`)) return;
    
    try {
      await api.delete(`/coupons/${coupon.id}`);
      toast.success('Cupom excluído');
      setCoupons(coupons.filter(c => c.id !== coupon.id));
    } catch (error) {
      toast.error('Erro ao excluir cupom');
    }
  };

  const handleToggleActive = async (coupon) => {
    try {
      await api.put(`/coupons/${coupon.id}`, { active: !coupon.active });
      setCoupons(coupons.map(c => 
        c.id === coupon.id ? { ...c, active: !c.active } : c
      ));
      toast.success(coupon.active ? 'Cupom desativado' : 'Cupom ativado');
    } catch (error) {
      toast.error('Erro ao atualizar cupom');
    }
  };

  const filteredCoupons = coupons.filter(c => 
    c.code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.customer_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const generateRandomCode = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let code = '';
    for (let i = 0; i < 8; i++) {
      code += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setFormData({ ...formData, code });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Cupons de Desconto</h1>
          <p className="text-muted-foreground">Gerencie cupons de desconto para renovações</p>
        </div>
        <Button onClick={() => openModal()} data-testid="new-coupon-btn">
          <Plus className="w-4 h-4 mr-2" />
          Novo Cupom
        </Button>
      </div>

      {/* Aviso sobre valor mínimo */}
      <Card className="border-amber-500/50 bg-amber-500/5">
        <CardContent className="py-3">
          <p className="text-sm text-amber-600 dark:text-amber-400">
            <strong>Atenção:</strong> Se o método de pagamento ativo for FastDePix, 
            valores com desconto abaixo de R$ 10,00 serão automaticamente ajustados para R$ 10,00.
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center gap-4">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Buscar cupom..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : filteredCoupons.length === 0 ? (
            <div className="text-center py-12">
              <Ticket className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
              <p className="text-muted-foreground">Nenhum cupom encontrado</p>
              <Button onClick={() => openModal()} variant="outline" className="mt-4">
                <Plus className="w-4 h-4 mr-2" />
                Criar primeiro cupom
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Código</TableHead>
                  <TableHead>Desconto</TableHead>
                  <TableHead>Cliente</TableHead>
                  <TableHead>Usos</TableHead>
                  <TableHead>Expira em</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="w-[100px]">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredCoupons.map((coupon) => (
                  <TableRow key={coupon.id}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Ticket className="w-4 h-4 text-primary" />
                        <span className="font-mono font-bold">{coupon.code}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary" className="font-mono">
                        {coupon.discount_percent}%
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {(coupon.customer_names && coupon.customer_names.length > 0) ? (
                        <div className="flex items-center gap-2">
                          <Users className="w-4 h-4 text-muted-foreground" />
                          <span className="truncate max-w-[150px]" title={coupon.customer_names.join(', ')}>
                            {coupon.customer_names.length === 1 
                              ? coupon.customer_names[0]
                              : `${coupon.customer_names.length} clientes`
                            }
                          </span>
                        </div>
                      ) : (coupon.customer_ids && coupon.customer_ids.length > 0) ? (
                        <div className="flex items-center gap-2">
                          <Users className="w-4 h-4 text-muted-foreground" />
                          <span>{coupon.customer_ids.length} cliente{coupon.customer_ids.length > 1 ? 's' : ''}</span>
                        </div>
                      ) : (coupon.target_type === 'plans' && coupon.target_ids && coupon.target_ids.length > 0) ? (
                        <span className="text-muted-foreground">{coupon.target_ids.length} plano(s)</span>
                      ) : (
                        <span className="text-muted-foreground">Todos</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <span className="font-mono">
                        {coupon.used_count}
                        {coupon.max_uses > 0 ? `/${coupon.max_uses}` : ' / ∞'}
                      </span>
                    </TableCell>
                    <TableCell>
                      {coupon.expires_at ? (
                        <span className="text-sm">
                          {(() => { const d = coupon.expires_at.split('T')[0].split('-'); return `${d[2]}/${d[1]}/${d[0]}`; })()}
                        </span>
                      ) : (
                        <span className="text-muted-foreground">Nunca</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Switch
                        checked={coupon.active}
                        onCheckedChange={() => handleToggleActive(coupon)}
                      />
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => openModal(coupon)}
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDelete(coupon)}
                          className="text-destructive hover:text-destructive"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Modal de Criação/Edição */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editing ? 'Editar Cupom' : 'Novo Cupom'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="code">Código do Cupom</Label>
              <div className="flex gap-2">
                <Input
                  id="code"
                  placeholder="EX: DESCONTO10"
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                  className="font-mono uppercase"
                />
                <Button type="button" variant="outline" onClick={generateRandomCode}>
                  Gerar
                </Button>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="discount">Porcentagem de Desconto (%)</Label>
              <div className="relative">
                <Percent className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  id="discount"
                  type="number"
                  min="1"
                  max="100"
                  placeholder="10"
                  value={formData.discount_percent}
                  onChange={(e) => setFormData({ ...formData, discount_percent: e.target.value })}
                  className="pl-9"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Users className="w-4 h-4" />
                Clientes Específicos (opcional)
              </Label>
              
              {/* Clientes selecionados */}
              {formData.customer_ids.length > 0 && (
                <div className="flex flex-wrap gap-2 p-2 bg-muted/50 rounded-lg">
                  {formData.customer_ids.map(id => (
                    <Badge key={id} variant="secondary" className="flex items-center gap-1">
                      {getCustomerName(id)}
                      <X 
                        className="w-3 h-3 cursor-pointer hover:text-destructive" 
                        onClick={() => toggleCustomer(id)}
                      />
                    </Badge>
                  ))}
                </div>
              )}
              
              {/* Busca de clientes */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Buscar cliente por nome ou telefone..."
                  value={customerSearchTerm}
                  onChange={(e) => setCustomerSearchTerm(e.target.value)}
                  className="pl-9"
                />
              </div>
              
              {/* Lista de clientes */}
              <ScrollArea className="h-40 border rounded-lg">
                <div className="p-2 space-y-1">
                  {customers.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-4">
                      Nenhum cliente encontrado
                    </p>
                  ) : filteredCustomers.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-4">
                      Nenhum resultado para "{customerSearchTerm}"
                    </p>
                  ) : (
                    filteredCustomers.slice(0, 50).map(customer => (
                      <label 
                        key={customer.id} 
                        className="flex items-center gap-2 p-2 hover:bg-muted/50 rounded cursor-pointer"
                      >
                        <Checkbox
                          checked={formData.customer_ids.includes(customer.id)}
                          onCheckedChange={() => toggleCustomer(customer.id)}
                        />
                        <span className="text-sm flex-1">{customer.name}</span>
                        <span className="text-xs text-muted-foreground">{customer.phone}</span>
                      </label>
                    ))
                  )}
                  {filteredCustomers.length > 50 && (
                    <p className="text-xs text-muted-foreground text-center py-2">
                      Mostrando 50 de {filteredCustomers.length} - refine sua busca
                    </p>
                  )}
                </div>
              </ScrollArea>
              
              <p className="text-xs text-muted-foreground">
                {formData.customer_ids.length === 0 
                  ? 'Deixe vazio para permitir todos os clientes usarem o cupom'
                  : `${formData.customer_ids.length} cliente(s) selecionado(s) - apenas eles poderão usar este cupom`
                }
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="max_uses">Quantidade de Usos (0 = ilimitado)</Label>
              <div className="relative">
                <Hash className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  id="max_uses"
                  type="number"
                  min="0"
                  placeholder="0"
                  value={formData.max_uses}
                  onChange={(e) => setFormData({ ...formData, max_uses: e.target.value })}
                  className="pl-9"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="expires_at">Data de Expiração (opcional)</Label>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  id="expires_at"
                  type="date"
                  value={formData.expires_at}
                  onChange={(e) => setFormData({ ...formData, expires_at: e.target.value })}
                  className="pl-9"
                />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setModalOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSubmit} disabled={saving}>
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Salvando...
                </>
              ) : (
                editing ? 'Salvar' : 'Criar Cupom'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
